import React, { useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  BusyBlocker,
  DefaultTemplate,
  CancelWizardDialog,
  ConfirmWizardDialog,
} from '@bingads-webui-react/wizard-ui-library';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withScope } from '@bingads-webui-react/scope-service';
import { MmpFetchProvider } from '@bingads-webui-campaign-react/xandr-campaign';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { withLoggingContext } from '@bingads-webui-campaign-react/copilot-loggers';
import { useConfirmDialogConfigWithLogger } from '@bingads-webui-campaign-react/campaign-settings-wizard-v1-utils';

import { getFlow } from '../step-utils';
import {
  getStepConfig,
  getStepConfigMultiAdFormat,
} from '../steps';
import { FLOWS_TO_FIRST_STEP } from '../step-constants';

const DisplayPlusWizardInternal = ({
  wizardType, flowType, routeToNavigateToWhenDone, initialWizardData, scope, perfMarker, permissions, children, adsType, loadClipchampPlayer,
}) => {
  const instrumentation = useContext(InstrumentationContext);
  const isEnableMultiAdFormat = permissions && permissions.dynamic && permissions.dynamic.MultiAdFormat && !permissions.IsAmplifyEnabled;
  const isConversionAssociationWarningEnabled = permissions && permissions.dynamic && permissions.dynamic.ConversionAssociationWarning;

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(`DisplayPlusWizard-${wizardType}`)
  ), [perfMarker, wizardType]);

  const initialStep = FLOWS_TO_FIRST_STEP[flowType];

  const steps = useMemo(() => (isEnableMultiAdFormat ? getStepConfigMultiAdFormat(permissions, wizardType, adsType, loadClipchampPlayer) : getStepConfig(permissions, wizardType, adsType, loadClipchampPlayer)), [isEnableMultiAdFormat, permissions, wizardType, adsType, loadClipchampPlayer]);

  const config = useMemo(() => ({
    id: 'display-plus-wizard',
    initialStep,
    steps,
    initialFlowFactors: { flow: flowType },
    initialWizardData: {
      flow: flowType,
      ...initialWizardData,
    },
    getFlow: ({ flow }) => getFlow({ flow, permissions }),
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: () => scope.save(routeToNavigateToWhenDone),
    },
    loadClipchampPlayer,
    attachStepActivities: false,
  }), [flowType, initialStep, initialWizardData, permissions, routeToNavigateToWhenDone, scope, steps, adsType, loadClipchampPlayer]);

  useEffect(() => {
    setClarityTag({ DisplayPlusCampaigns: flowType });
  }, [flowType]);

  return (
    <PerformanceMarkerProvider
      name="DisplayPlusXandrVideoAds"
      instrumentation={instrumentation}
      immediateDone={false}
    >
      <Scenario name={`DisplayPlus-${flowType}`}>
        <Activity name="DefaultWizardActivity">
          <MmpFetchProvider>
            <Wizard config={config}>
              <WizardPerfMarker wizardName="DisplayPlusWizard" wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout type="Blocker" component={BusyBlocker} loadingText="Processing" />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  {isConversionAssociationWarningEnabled && <Layout type="ConfirmDialog" component={ConfirmWizardDialog} config={useConfirmDialogConfigWithLogger} />}
                  {children}
                </LayoutContainer>
              </WizardPerfMarker>
            </Wizard>
          </MmpFetchProvider>
        </Activity>
      </Scenario>
    </PerformanceMarkerProvider>
  );
};

DisplayPlusWizardInternal.propTypes = {
  siteMap: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  wizardType: PropTypes.string.isRequired,
  flowType: PropTypes.string.isRequired,
  routeToNavigateToWhenDone: PropTypes.func.isRequired,
  initialWizardData: PropTypes.shape({}),
  permissions: PropTypes.shape({}).isRequired,
  children: PropTypes.node,
  adsType: PropTypes.string,
};

DisplayPlusWizardInternal.defaultProps = {
  initialWizardData: {},
  children: null,
  adsType: '',
};

export const DisplayPlusWizard = withPermissions(withScope(withLoggingContext(withRecommendationContext(DisplayPlusWizardInternal))));
